/**
 * DetailsScreenContent - Renders the user details form screen
 *
 * This component is extracted from ScreenRouter to fix a React hooks violation.
 * It encapsulates the useDetailsScreen hook to ensure hooks are always called
 * in the same order, regardless of the current screen.
 */
import React, { useEffect, useRef } from 'react';
import { Form } from 'react-final-form';

import Link from '@/components/Link';

import { AuthenticatingLoader, ExpertHeader, Stepper, UserDetailsForm } from '../';
import { useDetailsScreen } from '../../hooks';
import { useStyles } from '../../styles';
import type { Expert } from '../../types';
import type {
  MissingField,
  ProfileState,
  Screen,
  SignupFormData,
  StripeState,
  UserData,
} from '../../types';
import { trackSMBDetailsViewed } from '../../utils/smbAnalytics';

export interface DetailsScreenContentProps {
  expert: Expert;
  userData: UserData | null;
  profileState: ProfileState | null;
  isAuthenticatedUserMode: boolean;
  otpVerificationToken: string | null;
  signupFormData: SignupFormData | null;
  isLoading: boolean;
  error: string | null;
  missingFields: MissingField[];
  partialProfileData: {
    firstName?: string;
    lastName?: string;
    email?: string;
    company?: string;
  } | null;
  isExistingUserFromEmailLogin: boolean;
  billingAccount: any | null;
  onNavigate: (screen: Screen) => void;
  onSetStripeState: (state: StripeState) => void;
  onSetSignupFormData: (data: SignupFormData) => void;
  onSubmitNewUser: (values: {
    firstName: string;
    lastName: string;
    email: string;
    company: string;
    jobTitle?: string;
    password: string;
  }) => Promise<void>;
}

export const DetailsScreenContent: React.FC<DetailsScreenContentProps> = ({
  expert,
  userData,
  profileState,
  isAuthenticatedUserMode,
  otpVerificationToken,
  signupFormData,
  isLoading,
  error,
  missingFields,
  partialProfileData,
  isExistingUserFromEmailLogin,
  billingAccount,
  onNavigate,
  onSetStripeState,
  onSetSignupFormData,
  onSubmitNewUser,
}) => {
  const s = useStyles();

  const detailsScreen = useDetailsScreen({
    userData,
    profileState,
    isAuthenticatedUserMode,
    otpVerificationToken,
    signupFormData,
    isLoading,
    error,
    missingFields,
    partialProfileData,
    isExistingUserFromEmailLogin,
    billingAccount,
    onNavigate,
    onSetStripeState,
    onSetSignupFormData,
    onSubmitNewUser,
  });

  const hasTrackedView = useRef(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!hasTrackedView.current) {
      hasTrackedView.current = true;
      trackSMBDetailsViewed(
        detailsScreen.isExistingUserCompletingProfile,
        detailsScreen.missingFields || [],
        expert.id,
        expert.name,
        userData?.id || ''
      );
    }
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  if (detailsScreen.isLoading) {
    return (
      <>
        <ExpertHeader expert={expert} />
        <Stepper currentStep={detailsScreen.stepIndex} steps={detailsScreen.steps} />
        <AuthenticatingLoader />
      </>
    );
  }

  return (
    <>
      <ExpertHeader expert={expert} />
      <Stepper currentStep={detailsScreen.stepIndex} steps={detailsScreen.steps} />
      <Form
        key="user-details-form"
        onSubmit={detailsScreen.handleSubmit}
        validate={detailsScreen.validate}
        initialValues={detailsScreen.initialValues}
      >
        {({ handleSubmit: formHandleSubmit, valid }) => (
          <form onSubmit={formHandleSubmit}>
            {detailsScreen.error && (
              <div className={s.paymentError} style={{ margin: '0 24px 16px' }}>
                <p className={s.paymentErrorText}>{detailsScreen.error}</p>
                {detailsScreen.signInRequired && <Link href="/login">Go to sign in</Link>}
              </div>
            )}
            {!detailsScreen.signInRequired && (
              <UserDetailsForm
                onSubmit={formHandleSubmit}
                valid={valid && !detailsScreen.isSettingUpPayment}
                onBack={detailsScreen.handleBack}
                isExistingUser={detailsScreen.isExistingUserCompletingProfile}
                missingFields={detailsScreen.missingFields}
              />
            )}
          </form>
        )}
      </Form>
    </>
  );
};
